<form [formGroup]="formGroup">
  <div fxLayout="column">
    <div *ngIf="outdatedWarnings?.length > 0" class="mb-3" [class.text-warn]="outdated">
      <ul>
        <li *ngFor="let message of outdatedWarnings">{{ message | translate }}</li>
      </ul>

      <div *ngIf="outdated" fxLayout="row" fxLayoutAlign="end center">
        <button mat-stroked-button (click)="ignoreOutdatedConflicts.emit()">Ignore Conflicts</button>
      </div>
    </div>

    <div *ngIf="ignoredOutdatedConflicts" class="mb-2">
      Please save to ignore conflicts.
    </div>

    <div fxLayout="row" fxLayoutAlign="space-between center" fxLayoutGap="10px" class="mb-3">
      <mat-form-field fxFlex>
        <mat-label>
          {{ 'common.status' | translate }}
        </mat-label>
        <mat-select formControlName="status">
          <mat-option *ngFor="let item of authorizationStatuses" [value]="item.value">
            {{ item.viewValue | translate }}
          </mat-option>
        </mat-select>
        <mat-hint *ngIf="pending$ | async as nextPullAt">
          {{ 'dialog.authorization.next-pull-at' | translate: { value: nextPullAt | momentDateTime } }}
        </mat-hint>
      </mat-form-field>
      <mat-form-field fxFlex>
        <mat-label>
          {{ 'dialog.authorization.insurance.label' | translate }}
        </mat-label>
        <mat-select formControlName="insuranceId">
          <mat-option *ngFor="let insurance of insurances$ | async" [value]="insurance.id">
            {{ insurance.clinicCarrierName || insurance.clinicCarrierId }}
          </mat-option>
        </mat-select>
        <mat-hint>{{ 'dialog.authorization.insurance.hint' | translate }}</mat-hint>
      </mat-form-field>
    </div>
    <div fxLayout="row" fxLayoutAlign="space-between start" fxLayoutGap="10px">
      <mat-form-field fxFlex subscriptSizing="dynamic" class="mb-3">
        <mat-label>
          {{ 'dialog.authorization.number.label' | translate }}
        </mat-label>
        <input matInput autocomplete="off" formControlName="number" />
        <mat-hint>{{ 'dialog.authorization.number.hint' | translate }}</mat-hint>
        <mat-error>
          <mmx-form-error [control]="formGroup.get('number')"></mmx-form-error>
        </mat-error>
      </mat-form-field>

      <mat-form-field fxFlex subscriptSizing="dynamic" class="mb-3">
        <mat-label>
          {{ 'dialog.authorization.reference-number.label' | translate }}
        </mat-label>
        <input matInput autocomplete="off" formControlName="referenceNumber" />
        <mat-hint>{{ 'dialog.authorization.reference-number.hint' | translate }}</mat-hint>
        <mat-error>
          <mmx-form-error [control]="formGroup.get('referenceNumber')"></mmx-form-error>
        </mat-error>
      </mat-form-field>
    </div>
    <div fxLayout="row" fxLayoutAlign="space-between start" fxLayoutGap="10px">
      <mat-form-field fxFlex class="mb-3">
        <mat-label>
          {{ 'dialog.authorization.start-date.label' | translate }}
        </mat-label>
        <mmx-date-selector-input
          formControlName="startAt"
          [required]="formGroup.get('status').value === AUTHORIZATION_STATUS.APPROVED"
        ></mmx-date-selector-input>
        <mat-hint>
          {{ 'common.set-to' | translate }}:
          <a class="text-link" (click)="setTo($event, 'startAt', 0)">{{ 'common.today' | translate }}</a>,
          <a class="text-link" (click)="setTo($event, 'startAt', -1)">{{ 'common.yesterday' | translate }}</a>
        </mat-hint>
        <mat-error>
          <mmx-form-error [control]="formGroup.get('startAt')"></mmx-form-error>
        </mat-error>
      </mat-form-field>
      <mat-form-field fxFlex>
        <mat-label>
          {{ 'dialog.authorization.end-date.label' | translate }}
        </mat-label>
        <mmx-date-selector-input
          formControlName="endAt"
          [required]="formGroup.value.status === AUTHORIZATION_STATUS.APPROVED"
        ></mmx-date-selector-input>
        <mat-hint fxLayout="row" fxLayoutAlign="start center" style="width: 100%; padding-top: 5px">
          <div>
            {{ 'common.set-to' | translate }}:
            <a class="text-link" (click)="setTo($event, 'endAt', 0)">{{ 'common.today' | translate }}</a>,
            <a class="text-link" (click)="setTo($event, 'endAt', 6, 'months')">+6 {{ 'common.months' | translate }}</a>,
            <a class="text-link" (click)="setTo($event, 'endAt', 1, 'years')">+1 {{ 'common.year' | translate }}</a>
          </div>
          <div *ngIf="diff$ | async as diff" fxFlex class="text-right">
            {{ diff }} {{ 'common.days' | translate }}
          </div>
        </mat-hint>
        <mat-error>
          <mmx-form-error [control]="formGroup.get('endAt')"></mmx-form-error>
        </mat-error>
      </mat-form-field>
    </div>

    <mat-form-field fxFlex class="mb-3">
      <mat-label>{{ 'dialog.authorization.procedures.label' | translate }}</mat-label>
      <mmx-codes-selector formControlName="procedureCodes"></mmx-codes-selector>
      <mat-hint>{{ 'dialog.authorization.procedures.hint' | translate: { codes: procedureCodes.join(', ') } }}</mat-hint>
      <mat-error>
        <mmx-form-error [control]="formGroup.get('procedureCodes')"></mmx-form-error>
      </mat-error>
    </mat-form-field>

    <mat-form-field fxFlex class="mb-3">
      <mat-label>{{ 'dialog.authorization.diagnoses.label' | translate }}</mat-label>
      <mmx-codes-selector formControlName="diagnosisCodes" [validateCPT]="false"></mmx-codes-selector>
      <mat-hint>{{ 'dialog.authorization.diagnoses.hint' | translate: { codes: diagnosisCodes.join(', ') } }}</mat-hint>
      <mat-error>
        <mmx-form-error [control]="formGroup.get('procedureCodes')"></mmx-form-error>
      </mat-error>
    </mat-form-field>

    <mat-slide-toggle class="mb-5" [checked]="includeUnits" (toggleChange)="includeUnits = !includeUnits">
      {{ 'patient-details.authorization-card.include-units' | translate }}
    </mat-slide-toggle>

    <div *ngIf="includeUnits" fxLayout="row" fxLayoutAlign="space-between center" fxLayoutGap="10px" formGroupName="limitations">
      <mat-form-field fxFlex>
        <mat-label>
          {{ 'patient-details.authorization-card.quantity' | translate }}
        </mat-label>
        <input matInput type="number" formControlName="quantity" />
      </mat-form-field>
      <mat-form-field fxFlex>
        <mat-label>
          {{ 'patient-details.authorization-card.quantity-type' | translate }}
        </mat-label>
        <mat-select formControlName="quantityType">
          <mat-option [value]="'visit'">{{ 'patient-details.authorization-card.quantity-types.visit' | translate }}</mat-option>
          <mat-option [value]="'hour'">{{ 'patient-details.authorization-card.quantity-types.hour' | translate }}</mat-option>
          <mat-option [value]="'day'">{{ 'patient-details.authorization-card.quantity-types.day' | translate }}</mat-option>
          <mat-option [value]="'dosage'">{{ 'patient-details.authorization-card.quantity-types.dosage' | translate }}</mat-option>
          <mat-option [value]="'FL'">FL</mat-option>
        </mat-select>
      </mat-form-field>
    </div>

    <ng-container *ngIf="customFieldsService.authorizationFields$ | async as fields">
      <mmx-custom-fields-form
        fxFlex
        *ngIf="fields.length > 0"
        [fields]="fields"
        formControlName="customFields"
      >
        <header class="subheading text-primary uppercase text-xs">
          {{ 'common.custom-fields' | translate }}
        </header>
      </mmx-custom-fields-form>
    </ng-container>

    <mat-form-field>
      <mat-label>{{ 'common.notes' | translate }}</mat-label>
      <textarea matInput formControlName="note"></textarea>
    </mat-form-field>
  </div>
</form>
